import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Link } from "react-router-dom";
import { loadClarity } from "@/lib/clarity";
import { ClipboardList } from "@/components/Icon";

const STORAGE_KEY = "mp_consent_v1";

type Consent = {
  necessary: true;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
};

declare global {
  interface Window {
    openCookieSettings?: () => void;
  }
}

const applyConsent = (c: Consent) => {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gtag: (...args: any[]) => void =
    (window as any).gtag ||
    function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments as unknown as unknown[]);
    };
  gtag("consent", "update", {
    ad_storage: c.marketing ? "granted" : "denied",
    ad_user_data: c.marketing ? "granted" : "denied",
    ad_personalization: c.marketing ? "granted" : "denied",
    analytics_storage: c.analytics ? "granted" : "denied",
    functionality_storage: c.functional ? "granted" : "denied",
    personalization_storage: c.functional ? "granted" : "denied",
  });
  // Stuur een page_view zodra consent verleend is, zodat realtime direct iets toont
  if (c.analytics) {
    gtag("event", "page_view", {
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
    // Microsoft Clarity valt onder dezelfde analytische toestemming
    loadClarity();
  }
};

const save = (c: Consent) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  applyConsent(c);
};

const CookieBanner = () => {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [functional, setFunctional] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // During prerender: keep everything at defaults so the SSR snapshot renders
    // `null`, matching the client's initial hydration render exactly.
    if (typeof window !== "undefined" && (window as unknown as { __IS_PRERENDER__?: boolean }).__IS_PRERENDER__) {
      return;
    }
    setMounted(true);
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      setOpen(true);
    } else {
      try {
        const c = JSON.parse(stored) as Consent;
        setFunctional(!!c.functional);
        setAnalytics(!!c.analytics);
        setMarketing(!!c.marketing);
        // Eerder verleende analytische toestemming → Clarity direct laden
        if (c.analytics) loadClarity();
      } catch {
        setOpen(true);
      }
    }
    window.openCookieSettings = () => {
      setShowDetails(true);
      setOpen(true);
    };
  }, []);

  if (!mounted || !open) return null;

  const acceptAll = () => {
    save({ necessary: true, functional: true, analytics: true, marketing: true, timestamp: new Date().toISOString() });
    setOpen(false);
  };
  const rejectAll = () => {
    save({ necessary: true, functional: false, analytics: false, marketing: false, timestamp: new Date().toISOString() });
    setOpen(false);
  };
  const saveSelection = () => {
    save({ necessary: true, functional, analytics, marketing, timestamp: new Date().toISOString() });
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie-instellingen"
      className="fixed inset-x-0 bottom-0 z-[100] p-4 sm:p-6 pointer-events-none"
    >
      <div className="mx-auto max-w-3xl bg-white border border-border rounded-2xl shadow-2xl p-5 sm:p-6 pointer-events-auto">
        <div className="flex items-start gap-3 mb-3">
          <ClipboardList className="w-6 h-6 text-primary shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-bold text-[#0a2933]">Wij gebruiken cookies</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Wij gebruiken functionele cookies voor de werking van de website en — met uw toestemming —
              analytische en marketing cookies om onze dienstverlening te verbeteren. Lees meer in onze{" "}
              <Link to="/privacy" className="underline text-primary">privacyverklaring</Link>.
            </p>
          </div>
        </div>

        {showDetails && (
          <div className="space-y-3 my-4 border-t border-b border-border py-4">
            <Row label="Noodzakelijk" desc="Vereist voor de werking van de website. Kan niet worden uitgeschakeld.">
              <Switch checked disabled />
            </Row>
            <Row label="Functioneel" desc="Onthoudt voorkeuren zoals taal en offerte-mandje.">
              <Switch checked={functional} onCheckedChange={setFunctional} />
            </Row>
            <Row label="Analytisch" desc="Google Analytics en Microsoft Clarity — geanonimiseerd inzicht in websitegebruik.">
              <Switch checked={analytics} onCheckedChange={setAnalytics} />
            </Row>
            <Row label="Marketing" desc="Voor advertentiepersonalisatie en remarketing via Google.">
              <Switch checked={marketing} onCheckedChange={setMarketing} />
            </Row>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <Button variant="outline" onClick={() => setShowDetails((s) => !s)} className="sm:mr-auto">
            {showDetails ? "Verberg details" : "Instellingen"}
          </Button>
          <Button variant="outline" onClick={rejectAll}>Alleen noodzakelijk</Button>
          {showDetails && (
            <Button variant="secondary" onClick={saveSelection}>Selectie opslaan</Button>
          )}
          <Button onClick={acceptAll}>Alles accepteren</Button>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, desc, children }: { label: string; desc: string; children: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-4">
    <div>
      <div className="font-medium text-[#0a2933]">{label}</div>
      <div className="text-xs text-muted-foreground">{desc}</div>
    </div>
    <div className="pt-1">{children}</div>
  </div>
);

export default CookieBanner;
